import {
  Box,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import CustomStyledRadio from "../../ivr/CustomStyledRadio";
import {
  AccountBalance,
  AttachMoney,
  Cancel,
  Close,
  CreditCard,
  Payments,
} from "@mui/icons-material";
import { CreditCard as LucideCreditCard } from "lucide-react";
import Button from "../../../common/Button";
import CustomCurrencyField from "../../../common/CustomCurrencyField";
import { priceFormatter } from "../../../../utils/Utility";
import { useAppDispatch } from "../../../../redux/slice/redux-hooks";
import {
  CodeTable,
  fetchAllCodeTablesValues,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../../../redux/slice/codetableSlice";
import { useModalStyles } from "../../../common/useModalStyles";

interface StripePaymentTypePopupProps {
  open: boolean;
  onClose: () => void;
  invoiceData?: any | null;
  // CS-1845 - Returns Promise<void> to support async upfront payment flow
  // (addOrder → stripeCheckOutPaymentData in sequence)
  createCheckoutSession: (
    invoiceId: number,
    paymentMethodType: string,
    paymentType: string,
    paymentAmount: number
  ) => Promise<void> | void;
  isFromBillingDetails?: boolean;
  isUpfrontPayment?: boolean;
  isProcessingPayment?: boolean; // CS-1845 - Loading state to prevent UI interaction during payment processing
  checkoutError?: string | null; // CS-1845 - Error message to display inside the popup on failure
  onErrorCancel?: () => void;   // CS-1845 - Called when user clicks Cancel after an error (navigates to order list)
}

const formatMethodLabel = (code: string) => {
  let s = code.trim().replace(/_/g, " ");
  s = s.replace(/\bUs\b/g, "US");
  return s;
};

const resolvePaymentIcon = (code: string): JSX.Element => {
  const c = code.toUpperCase();
  if (c.includes("CARD") || c.includes("CREDIT") || c.includes("DEBIT")) return <CreditCard />;
  if (c.includes("BANK") || c.includes("ACH") || c.includes("US_BANK_ACCOUNT")) return <AccountBalance />;
  return <Payments />;
};

const getPaymentMethodUI = (code: string) => ({
  label: code === "US_BANK_ACCOUNT" ? "ACH" : formatMethodLabel(code),
  leftIcon: resolvePaymentIcon(code),
});

type FeeKeys = "percent" | "additional" | "max_charge" | "min_charge"; // CS-1563
type FeeConfig = Partial<Record<FeeKeys, number>>;

const parseNumber = (val: string) => {
  const n = Number(String(val).replace(/[%$,\s]/g, ""));
  return Number.isFinite(n) ? n : undefined;
};

const FEE_PREFIX = "platform_fee_";

const getMethodFeesDynamic = (stripeConfig: any, methodCode: string): FeeConfig => {
  const out: FeeConfig = {};
  if (!stripeConfig || stripeConfig.platform_fee_applicable !== "Y") return out;
  const methodKey = methodCode.toLowerCase();
  const prefix = `${FEE_PREFIX}${methodKey}_`;
  for (const key of Object.keys(stripeConfig)) {
    if (!key.toLowerCase().startsWith(prefix)) continue;
    const tail = key.substring(prefix.length);
    const num = parseNumber(stripeConfig[key]);
    if (num !== undefined) out[tail as FeeKeys] = num;
  }
  return out;
};

const isPositive = (n?: number) => typeof n === "number" && n > 0;

const StripePaymentTypePopup: React.FC<StripePaymentTypePopupProps> = ({
  open,
  onClose,
  invoiceData,
  createCheckoutSession,
  isFromBillingDetails,
  isUpfrontPayment,
  isProcessingPayment,
  checkoutError,
  onErrorCancel,
}) => {
  const { handleSubmit, control, getValues, setValue, watch, trigger } = useForm();
  const { tokens: t, paperSx, headerSx, iconBoxSx, bodySx, footerSx, closeIconSx, primaryBtnSx, cancelBtnSx } =
    useModalStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const { allCodeTablesValuesMap } = useCodetablesSlice();

  const stripeConfig = invoiceData?.stripeConfig || invoiceData?.manufacturerStripeConfig;
  const methodCfg = stripeConfig?.payment_method || "";
  const invoiceAmount = isFromBillingDetails ? invoiceData?.outstandingAmt : invoiceData?.actualRemainingAmount;
  const invoiceBalance = invoiceData?.invoiceBalance || invoiceAmount;

  const paymentOptions = useMemo(() => {
    if (!methodCfg) return [];

    // CS-1845 - Handle both string and array formats for payment_method configuration
    let methods: string[] = [];
    if (Array.isArray(methodCfg)) {
      methods = methodCfg.map(m => String(m)).filter(Boolean);
    } else if (typeof methodCfg === 'string') {
      methods = methodCfg.split(",").map((s: string) => s.trim()).filter(Boolean);
    }

    return methods
      .map((code: string) => { const ui = getPaymentMethodUI(code); return { value: code, label: ui.label, leftIcon: ui.leftIcon }; })
      .sort((a: any, b: any) => a.value === "US_BANK_ACCOUNT" ? -1 : b.value === "US_BANK_ACCOUNT" ? 1 : 0);
  }, [methodCfg]);

  // Track whether payment methods were ever successfully loaded in this popup session.
  // Once true it stays true — so a post-API-failure re-render never shows the
  // "no payment methods configured" message when methods were already visible.
  const methodsEverLoadedRef = useRef(false);
  if (paymentOptions.length > 0) {
    methodsEverLoadedRef.current = true;
  }

  useEffect(() => { dispatch(fetchAllCodeTablesValues()); }, []);

  const paymentTypeOptions = useMemo(() => {
    let statuses: CodeTable[] = [];
    if (allCodeTablesValuesMap)
      statuses = getCodeTableValuesByTableName(allCodeTablesValuesMap, "PaymentType");
    if (!statuses || statuses.length === 0) dispatch(fetchAllCodeTablesValues());
    let options: { label: string; value: any }[] = [];
    statuses?.map((option) => { options.push({ label: option.displayValue, value: option.keyName }); });
    return options;
  }, [allCodeTablesValuesMap, dispatch]);

  useEffect(() => {
    const allowed = new Set(paymentOptions.map((o: any) => o.value));
    const current = getValues("paymentMethodType");
    if (!allowed.has(current) && paymentOptions.length)
      setValue("paymentMethodType", paymentOptions[0].value, { shouldValidate: true });
    if (!getValues("paymentType"))
      setValue("paymentType", "FULL", { shouldValidate: true });
  }, [paymentOptions, getValues, setValue]);

  const selectedMethod = watch("paymentMethodType");
  const paymentType = watch("paymentType");
  const partialAmount = watch("partialAmount");

  useEffect(() => {
    if (paymentType === "PARTIAL" && partialAmount !== undefined) trigger("partialAmount");
  }, [partialAmount, paymentType, trigger]);

  const selectedFees = useMemo(
    () => getMethodFeesDynamic(stripeConfig, selectedMethod || ""),
    [stripeConfig, selectedMethod]
  );

  const paymentAmount = useMemo(() => {
    if (paymentType === "PARTIAL" && partialAmount) return Number(partialAmount);
    return invoiceBalance;
  }, [paymentType, partialAmount, invoiceBalance]);

  const calculatedFees = useMemo(() => {
    if (!paymentAmount || paymentAmount <= 0) return { feeAmount: 0, totalAmount: 0 };
    const percentFee = selectedFees.percent ?? 0;
    const additionalFee = selectedFees.additional ?? 0;
    const maxCharge = selectedFees.max_charge ?? 0;
    const minCharge = selectedFees.min_charge ?? 0;
    const isACH = selectedMethod === "US_BANK_ACCOUNT";
    const percentageFeeDecimal = percentFee / 100;
    let feeAmount = 0;
    if (isPositive(percentFee) || isPositive(additionalFee))
      feeAmount = ((paymentAmount + additionalFee) / (1 - percentageFeeDecimal)) - paymentAmount;
    if (isPositive(maxCharge) && feeAmount > maxCharge) feeAmount = maxCharge;
    if (isPositive(minCharge) && feeAmount < minCharge) feeAmount = minCharge;
    let totalAmount = paymentAmount + feeAmount;
    if (isACH && isPositive(percentFee)) {
      const achFee = (paymentAmount * percentFee) / 100;
      totalAmount = paymentAmount + (achFee >= 5 ? 5 : achFee);
    }
    return { feeAmount, totalAmount };
  }, [paymentAmount, selectedFees, selectedMethod]);

  const feeText = useMemo(() => {
    const parts: string[] = [];
    const isACH = selectedMethod === "US_BANK_ACCOUNT";
    if (isPositive(selectedFees.percent)) parts.push(`${selectedFees.percent}%`);
    if (isPositive(selectedFees.additional)) parts.push(`${priceFormatter(Number(selectedFees.additional))}`);
    const main = parts.join(" + ");
    if (!main) return "";
    if (isACH && isPositive(selectedFees.percent))
      return `Estimated Stripe Processing Fees: ${selectedFees.percent}% capped at ${priceFormatter(5)}`;
    return `Estimated Stripe Processing Fees: ${main}`;
  }, [selectedFees, selectedMethod]);

  const remainingBalance = useMemo(() => {
    if (paymentAmount > 0 && paymentAmount <= invoiceBalance) return invoiceBalance - paymentAmount;
    return invoiceBalance;
  }, [invoiceBalance, paymentAmount]);

  // CS-1845 - onSubmit is async to properly await createCheckoutSession (handleCheckout),
  // which calls addOrder then stripeCheckOutPaymentData in sequence for the upfront payment flow.
  const onSubmit = async (data: any) => {
    if (!data?.paymentMethodType) {
      console.error("CS-1845 - Missing payment method type", { paymentMethodType: data?.paymentMethodType });
      return;
    }

    const amountToCharge =
      data?.paymentType === "PARTIAL" && data?.partialAmount
        ? Number(data.partialAmount)
        : invoiceBalance;

    // CS-1845 START - For upfront payment flow the order has not been created yet,
    // so invoiceId is not available at this point. Pass 0 as a sentinel value;
    // handleCheckout in Order.tsx will call addOrder first, obtain the real invoiceId
    // from the response, and then proceed with the Stripe checkout call.
    if (isUpfrontPayment && !invoiceData?.invoiceId) {
      await createCheckoutSession(0, data?.paymentMethodType, data?.paymentType, amountToCharge);
      return;
    }
    // CS-1845 END

    if (invoiceData?.invoiceId) {
      await createCheckoutSession(invoiceData.invoiceId, data?.paymentMethodType, data?.paymentType, amountToCharge);
    } else {
      console.error("CS-1845 - Missing invoice ID for non-upfront payment flow", {
        invoiceId: invoiceData?.invoiceId,
        paymentMethodType: data?.paymentMethodType,
      });
    }
  };

  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography sx={{
      fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.08em",
      textTransform: "uppercase", color: t.textSecondary, mb: 1.5,
    }}>
      {children}
    </Typography>
  );

  const isPartialVisible = paymentType === "PARTIAL";
  const isPartialValid =
    isPartialVisible && partialAmount && Number(partialAmount) > 0 && Number(partialAmount) <= invoiceBalance;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
 
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(t.accent)}>
            <LucideCreditCard size={20} color={t.accent} strokeWidth={1.8} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: t.textPrimary, lineHeight: 1.3 }}>
                Payment Details
              </Typography>
              {invoiceBalance && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center",
                  px: 1, py: 0.25, borderRadius: "6px",
                  backgroundColor: `${t.accent}1a`,
                  border: `1px solid ${t.accent}33`,
                }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: t.accent }}>
                    {priceFormatter(invoiceBalance)}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: "0.8rem", color: t.textSecondary, mt: 0.15 }}>
              Select payment type and method to proceed
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={closeIconSx} disabled={isProcessingPayment}>
            <Close sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>
 
        <Box
          sx={{
            ...bodySx,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1",
              borderRadius: 4,
            },
          }}
        >
 
          {/* CS-1845 - Error state: replaces the normal body when checkout fails */}
          {checkoutError ? (
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              py: 3,
            }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: "50%",
                backgroundColor: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Cancel sx={{ color: "#EF4444", fontSize: 26 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: t.textPrimary, textAlign: "center" }}>
                Payment Failed
              </Typography>
              <Box sx={{
                p: 1.5, borderRadius: "10px", width: "100%",
                backgroundColor: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.2)",
              }}>
                <Typography sx={{ fontSize: "0.84rem", color: "#EF4444", textAlign: "center" }}>
                  {checkoutError}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.78rem", color: t.textSecondary, textAlign: "center" }}>
                Your order has been saved. Click Cancel to go to the order list.
              </Typography>
            </Box>
          ) : (
            <>
              {!isUpfrontPayment && (
                <>
                  <Box>
                    <SectionLabel>Pay Type</SectionLabel>
                    <CustomStyledRadio
                      control={control}
                      controllerName="paymentType"
                      defaultValue="FULL"
                      rules={{ required: "Please select a payment type" }}
                      options={paymentTypeOptions}
                      isDashboard
                    />
                  </Box>
         
                  <Box>
                    <SectionLabel>Amount</SectionLabel>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <CustomCurrencyField
                          control={control}
                          controllerName="fullAmount"
                          label="Outstanding Amount"
                          externalValue={invoiceBalance}
                          isDisabled
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ visibility: isPartialVisible ? "visible" : "hidden" }}>
                          <CustomCurrencyField
                            control={control}
                            controllerName="partialAmount"
                            label="Partial Amount"
                            size="small"
                            rules={{
                              required: isPartialVisible ? "Amount is required" : false,
                              validate: isPartialVisible
                                ? {
                                    minAmount: (v: any) => Number(v) >= 0.5 || "Amount must be at least $0.50",
                                    notEqualToOutstanding: (v: any) =>
                                      Number(v) !== invoiceBalance || "Amount cannot equal outstanding. Use FULL instead.",
                                    maxAmount: (v: any) =>
                                      Number(v) <= invoiceBalance || `Amount cannot exceed ${priceFormatter(invoiceBalance)}`,
                                  }
                                : undefined,
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
        
                    {isPartialValid && (
                      <Box sx={{
                        mt: 1.2, px: 1.4, py: 0.8, borderRadius: "8px",
                        minHeight: "36px",
                        backgroundColor: `${t.accent}0d`,
                        border: `1px solid ${t.accent}22`,
                      }}>
                        <Typography sx={{ fontSize: "0.8rem", color: t.textSecondary }}>
                          Remaining after payment:&nbsp;
                          <Box component="span" sx={{ fontWeight: 700, color: t.accent }}>
                            {priceFormatter(remainingBalance)}
                          </Box>
                        </Typography>
                      </Box>
                    )}
                  </Box>
        
                  <Divider sx={{ borderColor: t.border }} />
                </>
              )}
     
              <Box>
                <SectionLabel>Pay Method</SectionLabel>
                {paymentOptions.length > 0 ? (
                  <CustomStyledRadio
                    control={control}
                    controllerName="paymentMethodType"
                    defaultValue={paymentOptions[0]?.value}
                    rules={{ required: "Please select a payment method" }}
                    options={paymentOptions}
                    isDashboard
                  />
                ) : (
                  <Box sx={{
                    p: 1.5, borderRadius: "10px",
                    backgroundColor: isProcessingPayment || methodsEverLoadedRef.current
                      ? "rgba(20,184,166,0.06)"
                      : "rgba(239,68,68,0.08)",
                    border: isProcessingPayment || methodsEverLoadedRef.current
                      ? "1px solid rgba(20,184,166,0.18)"
                      : "1px solid rgba(239,68,68,0.2)",
                  }}>
                    <Typography sx={{
                      fontSize: "0.84rem",
                      color: isProcessingPayment || methodsEverLoadedRef.current ? t.textSecondary : "#EF4444",
                      textAlign: "center",
                    }}>
                      {isProcessingPayment
                        ? "Processing payment, please wait..."
                        : methodsEverLoadedRef.current
                          ? "Payment is being processed..."
                          : "No payment methods are configured for this manufacturer."
                      }
                    </Typography>
                    {!isProcessingPayment && !methodsEverLoadedRef.current && (
                      <Typography sx={{ fontSize: "0.75rem", color: "#EF4444", textAlign: "center", mt: 0.5 }}>
                        Please contact support or try again later.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
     
              {paymentAmount > 0 && (
                <>
                  <Box sx={{
                    p: 1.5, borderRadius: "10px",
                    backgroundColor: "rgba(20,184,166,0.06)",
                    border: "1px solid rgba(20,184,166,0.18)",
                  }}>
                    <Typography sx={{ fontSize: "0.84rem", color: feeText ? t.textPrimary : t.textSecondary }}>
                      {feeText || "We are not collecting any additional fees for this payment type as of now."}
                    </Typography>
                  </Box>

                  <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: 1,
                    p: 1.8, borderRadius: "12px",
                    backgroundColor: t.cardBg,
                    border: `1px solid ${t.border}`,
                  }}>
                    <Box>
                      <Typography sx={{
                        fontSize: "0.72rem", fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.06em",
                        color: t.textSecondary,
                      }}>
                        Estimated Total (incl. Stripe processing fees)
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: t.textSecondary, mt: 0.3 }}>
                        Ensure sufficient credit limit for this transaction.
                      </Typography>
                    </Box>
                    <Typography sx={{
                      fontSize: "1.5rem", fontWeight: 800,
                      color: t.accent, letterSpacing: "-0.02em",
                    }}>
                      {priceFormatter(calculatedFees.totalAmount)}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
 
        <Box sx={footerSx}>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
            {!checkoutError && (
              <Button
                color="success"
                type="submit"
                startIcon={<AttachMoney />}
                disabled={
                  isProcessingPayment ||
                  paymentOptions.length === 0 ||
                  (paymentType === "PARTIAL" && (!partialAmount || Number(partialAmount) <= 0))
                }
                sx={{ ...primaryBtnSx, minWidth: 130 }}
              >
                {isProcessingPayment ? "Processing..." : "Pay Now"}
              </Button>
            )}
            <Button
              color="error"
              onClick={checkoutError && onErrorCancel ? onErrorCancel : onClose}
              variant="outlined"
              startIcon={<Cancel />}
              sx={{ ...cancelBtnSx, minWidth: 110 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default StripePaymentTypePopup;